import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ChevronRight, Zap, DollarSign } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const inputCls =
  "rounded-none border-[#1F1F1F] bg-[#0A0A0A] text-white focus-visible:ring-0 focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

function ScenarioEditor({ title, scenario, setScenario, materials, hdd, badgeColor }) {
  const { t } = useI18n();
  const addLayer = () => {
    setScenario({ ...scenario, layers: [...scenario.layers, {
      id: crypto.randomUUID(), name: t("compare.newlayer", "New Layer"), thickness: 0.05, lambda_value: 0.04, mu: 1,
    }]});
  };
  const upd = (id, key, value) => {
    setScenario({ ...scenario, layers: scenario.layers.map(l =>
      l.id === id ? { ...l, [key]: key === "name" ? value : Number(value) } : l) });
  };
  const remove = (id) => setScenario({ ...scenario, layers: scenario.layers.filter(l => l.id !== id) });
  const applyMat = (id, name) => {
    const m = materials.find(mm => mm.name === name);
    if (!m) return;
    setScenario({ ...scenario, layers: scenario.layers.map(l =>
      l.id === id ? { ...l, name: m.name, lambda_value: m.lambda_value, mu: m.mu ?? 1 } : l) });
  };

  return (
    <div className="border border-[#1F1F1F] bg-[#141414] p-4" data-testid={`scenario-${scenario.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-6" style={{ background: badgeColor }}/>
          <Input value={scenario.name} onChange={e => setScenario({ ...scenario, name: e.target.value })}
            className={inputCls + " w-44"} data-testid={`scenario-name-${scenario.id}`}/>
        </div>
        <Button onClick={addLayer}
          className="rounded-none bg-[#0A0A0A] hover:bg-[#1F1F1F] border border-[var(--neon-cyan)] text-[var(--neon-cyan)] h-8 px-2 font-mono text-[10px] uppercase">
          <Plus className="w-3.5 h-3.5 mr-1"/> {t("compare.layer", "Layer")}
        </Button>
      </div>

      <div className="border border-[#1F1F1F]">
        <div className="grid grid-cols-12 gap-1 px-2 py-1 bg-[#0A0A0A] tech-label text-[9px]">
          <div className="col-span-1">#</div>
          <div className="col-span-5">{t("compare.col.material", "MATERIAL")}</div>
          <div className="col-span-2">D</div>
          <div className="col-span-2">λ</div>
          <div className="col-span-2 text-right">{t("compare.col.delete", "DELETE")}</div>
        </div>
        {scenario.layers.map((l, idx) => (
          <div key={l.id} className="grid grid-cols-12 gap-1 items-center px-2 py-1 border-t border-[#1F1F1F]">
            <div className="col-span-1 font-mono text-[10px] text-[#8B8B95]">L{idx+1}</div>
            <div className="col-span-5">
              <Select onValueChange={(v) => applyMat(l.id, v)}>
                <SelectTrigger className={inputCls + " h-7 text-[11px]"}>
                  <SelectValue placeholder={l.name}/>
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-60 bg-[#0A0A0A] text-white border-[#1F1F1F]">
                  {materials.map(m => (
                    <SelectItem key={m.id} value={m.name} className="rounded-none font-mono text-[11px]">{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Input type="number" step="0.005" value={l.thickness}
                onChange={(e) => upd(l.id, "thickness", e.target.value)} className={inputCls + " h-7 text-[11px]"}/>
            </div>
            <div className="col-span-2">
              <Input type="number" step="0.001" value={l.lambda_value}
                onChange={(e) => upd(l.id, "lambda_value", e.target.value)} className={inputCls + " h-7 text-[11px]"}/>
            </div>
            <div className="col-span-2 text-right">
              <button onClick={() => remove(l.id)} className="p-1 hover:bg-[#FF3B30]/20">
                <Trash2 className="w-3 h-3 text-[#FF3B30]"/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {scenario.metrics && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="border border-[#1F1F1F] p-2">
            <div className="tech-label text-[9px]">{t("compare.cell.r", "R-VALUE")}</div>
            <div className="font-mono text-xl font-semibold neon-cyan">{scenario.metrics.total_r.toFixed(3)}</div>
          </div>
          <div className="border border-[#1F1F1F] p-2">
            <div className="tech-label text-[9px]">{t("compare.cell.u", "U-VALUE")}</div>
            <div className="font-mono text-xl font-semibold" style={{ color: badgeColor }}>
              {scenario.metrics.u_value.toFixed(3)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompareView({ materials, hdd = 2716, electricityPrice = 2.5 }) {
  const { t } = useI18n();
  const [scenarioA, setScenarioA] = useState({
    id: "A", name: "Scenario A · Stone Wool 5cm",
    layers: [
      { id: crypto.randomUUID(), name: "Gypsum Plaster", thickness: 0.02, lambda_value: 0.51, mu: 10 },
      { id: crypto.randomUUID(), name: "Horizontal Perforated Brick", thickness: 0.19, lambda_value: 0.45, mu: 8 },
      { id: crypto.randomUUID(), name: "Stone Wool Slab", thickness: 0.05, lambda_value: 0.038, mu: 1 },
    ],
  });
  const [scenarioB, setScenarioB] = useState({
    id: "B", name: "Scenario B · Graphite EPS 8cm",
    layers: [
      { id: crypto.randomUUID(), name: "Gypsum Plaster", thickness: 0.02, lambda_value: 0.51, mu: 10 },
      { id: crypto.randomUUID(), name: "Horizontal Perforated Brick", thickness: 0.19, lambda_value: 0.45, mu: 8 },
      { id: crypto.randomUUID(), name: "Graphite EPS (Silver EPS)", thickness: 0.08, lambda_value: 0.031, mu: 40 },
    ],
  });

  const [area, setArea] = useState(100);
  const [price, setPrice] = useState(electricityPrice);

  useEffect(() => {
    const calc = async (s) => {
      try {
        const { data } = await api.post("/calculate", {
          layers: s.layers.map(({ name, thickness, lambda_value, mu }) =>
            ({ name, thickness, lambda_value, mu: mu ?? 1 })),
          indoor_temp: 20, outdoor_temp: 0, indoor_humidity: 50, outdoor_humidity: 80,
        });
        return data;
      } catch { return null; }
    };
    let mounted = true;
    Promise.all([calc(scenarioA), calc(scenarioB)]).then(([a, b]) => {
      if (!mounted) return;
      setScenarioA((s) => ({ ...s, metrics: a }));
      setScenarioB((s) => ({ ...s, metrics: b }));
    });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scenarioA.layers), JSON.stringify(scenarioB.layers)]);

  const compare = useMemo(() => {
    if (!scenarioA.metrics || !scenarioB.metrics) return null;
    // Annual heating energy (simplified): Q = U·A·HDD·24/1000 kWh/year
    const energyA = (scenarioA.metrics.u_value * area * hdd * 24) / 1000;
    const energyB = (scenarioB.metrics.u_value * area * hdd * 24) / 1000;
    const energyDiff = energyA - energyB;          // positive if B saves
    const costDiff = energyDiff * price;
    return {
      energyA: Math.round(energyA),
      energyB: Math.round(energyB),
      energyDiff: Math.round(energyDiff),
      costDiff: Math.round(costDiff),
      better: scenarioB.metrics.u_value < scenarioA.metrics.u_value ? "B" : "A",
      uDiff: scenarioA.metrics.u_value - scenarioB.metrics.u_value,
    };
  }, [scenarioA.metrics, scenarioB.metrics, area, hdd, price]);

  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="border border-[#1F1F1F] bg-[#141414] p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold tracking-tight">{t("compare.params", "Comparison Parameters")}</h2>
          <span className="tech-label compare-pink">HDD: {hdd}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="tech-label compare-cyan"
              data-testid="compare-label-area">
              {t("compare.area", "Area (m²)")}
            </Label>
            <Input type="number" value={area} onChange={e => setArea(Number(e.target.value))}
              className={inputCls + " compare-pink"}
              data-testid="compare-area"/>
          </div>
          <div>
            <Label className="tech-label compare-cyan"
              data-testid="compare-label-electricity">
              {t("compare.electricity", "Electricity (TL/kWh)")}
            </Label>
            <Input type="number" step="0.1" value={price} onChange={e => setPrice(Number(e.target.value))}
              className={inputCls + " compare-pink"}
              data-testid="compare-price"/>
          </div>
          <div>
            <Label className="tech-label compare-cyan"
              data-testid="compare-label-heating-duration">
              {t("compare.heating.duration", "Annual Heating Duration")}
            </Label>
            <div className={inputCls + " compare-pink flex items-center"}
              data-testid="compare-hdd-value">
              {hdd} HDD · 24h
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ScenarioEditor title="A" scenario={scenarioA} setScenario={setScenarioA} materials={materials}
          hdd={hdd} badgeColor="var(--neon-cyan)"/>
        <ScenarioEditor title="B" scenario={scenarioB} setScenario={setScenarioB} materials={materials}
          hdd={hdd} badgeColor="var(--neon-green)"/>
      </div>

      {compare && (
        <div className="border border-[var(--neon-green)] bg-[#0A1A0E] p-5" data-testid="compare-results">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold tracking-tight"
              data-testid="compare-result-heading"
              style={{
                color: '#EC4899',
                WebkitTextFillColor: '#EC4899',
                fontWeight: 700,
                fontSize: '1.25rem',
                opacity: 1,
                visibility: 'visible',
                display: 'inline-block',
                position: 'relative',
                zIndex: 10
              }}>
              {t("compare.result", "Result Comparison")}
            </h3>
            <span className="font-mono text-xs uppercase tracking-wider neon-green">
              {t("compare.winning", "WINNING: SCENARIO")} {compare.better}
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F1F1F] border border-[#1F1F1F]">
            <div className="bg-[#0A0A0A] p-3">
              <div className="tech-label compare-cyan">ΔU (W/m²K)</div>
              <div className="font-mono text-2xl" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 700, opacity: 1 }}>
                {compare.uDiff > 0 ? "−" : "+"}{Math.abs(compare.uDiff).toFixed(3)}
              </div>
            </div>
            <div className="bg-[#0A0A0A] p-3">
              <div className="tech-label compare-cyan">A · {t("compare.annual", "Annual (kWh)")}</div>
              <div className="font-mono text-2xl" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 700, opacity: 1 }}>{compare.energyA.toLocaleString("tr-TR")}</div>
            </div>
            <div className="bg-[#0A0A0A] p-3">
              <div className="tech-label compare-cyan">B · {t("compare.annual", "Annual (kWh)")}</div>
              <div className="font-mono text-2xl" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 700, opacity: 1 }}>{compare.energyB.toLocaleString("tr-TR")}</div>
            </div>
            <div className="bg-[#0A0A0A] p-3">
              <div className="tech-label compare-cyan flex items-center gap-1"><DollarSign className="w-3 h-3"/> {t("compare.savings", "SAVINGS / YEAR")}</div>
              <div className="font-mono text-2xl" data-testid="compare-savings" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 700, opacity: 1 }}>
                {compare.costDiff.toLocaleString("tr-TR")} <span className="text-sm" style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899' }}>TL</span>
              </div>
            </div>
          </div>
          <p className="font-mono text-[11px] text-[#8B8B95] mt-3 leading-relaxed">
            {t("compare.calc", "Calculation: Q = U · A · HDD · 24 / 1000 (kWh/year). 20-year projection: {value} potential savings · energy price inflation not included.").split("{value}").map((part, i, arr) =>
              i < arr.length - 1
                ? <React.Fragment key={i}>{part}<span style={{ color: '#EC4899', WebkitTextFillColor: '#EC4899', fontWeight: 700 }}>{(compare.costDiff * 20).toLocaleString("tr-TR")} TL</span></React.Fragment>
                : <React.Fragment key={i}>{part}</React.Fragment>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
